import * as joint from '@joint/core'

/**
 * C4 DeploymentNode shape - nested container for deployment diagrams
 * Represents infrastructure like servers, cloud regions, containers
 */
export const C4DeploymentNode = joint.dia.Element.define(
  'c4.DeploymentNode',
  {
    size: { width: 400, height: 300 },
    attrs: {
      body: {
        refWidth: '100%',
        refHeight: '100%',
        rx: 5,
        ry: 5,
        fill: '#ffffff',
        stroke: '#888888',
        strokeWidth: 2,
      },
      // 3D effect - top face
      topFace: {
        refX: 0,
        refY: 0,
        refWidth: '100%',
        height: 8,
        fill: '#f5f5f5',
        stroke: '#888888',
        strokeWidth: 1,
      },
      // 3D effect - right face
      rightFace: {
        refX: '100%',
        refY: 0,
        width: 8,
        refHeight: '100%',
        fill: '#e0e0e0',
        stroke: '#888888',
        strokeWidth: 1,
      },
      titleBackground: {
        refX: 0,
        refY: 0,
        refWidth: '100%',
        height: 40,
        fill: '#f5f5f5',
        stroke: 'none',
      },
      nameLabel: {
        refX: 10,
        refY: 12,
        textVerticalAnchor: 'top',
        textAnchor: 'start',
        fontSize: 14,
        fontWeight: 'bold',
        fill: '#333333',
        fontFamily: 'Open Sans, sans-serif',
        text: '',
      },
      typeLabel: {
        refX: 10,
        refY: 28,
        textVerticalAnchor: 'top',
        textAnchor: 'start',
        fontSize: 11,
        fill: '#666666',
        fontFamily: 'Open Sans, sans-serif',
        text: '[Deployment Node]',
      },
      instancesLabel: {
        refX: 'calc(w - 10)',
        refY: 12,
        textVerticalAnchor: 'top',
        textAnchor: 'end',
        fontSize: 11,
        fill: '#666666',
        fontFamily: 'Open Sans, sans-serif',
        text: '',
      },
    },
  },
  {
    markup: [
      { tagName: 'rect', selector: 'body' },
      { tagName: 'rect', selector: 'topFace' },
      { tagName: 'rect', selector: 'rightFace' },
      { tagName: 'rect', selector: 'titleBackground' },
      { tagName: 'text', selector: 'nameLabel' },
      { tagName: 'text', selector: 'typeLabel' },
      { tagName: 'text', selector: 'instancesLabel' },
    ],
  }
)

export interface DeploymentNodeStyle {
  background?: string
  stroke?: string
  color?: string
}

export function createDeploymentNode(
  id: string,
  name: string,
  technology: string = '',
  instances: string = '',
  x: number = 0,
  y: number = 0,
  width: number = 400,
  height: number = 300,
  style?: DeploymentNodeStyle
): joint.dia.Element {
  const typeText = technology ? `[${technology}]` : '[Deployment Node]'
  const instancesText = instances && instances !== '1' ? `x${instances}` : ''

  const deploymentNode = new C4DeploymentNode({
    id,
    position: { x, y },
    size: { width, height },
    attrs: {
      body: {
        ...(style?.background && { fill: style.background }),
        ...(style?.stroke && { stroke: style.stroke }),
      },
      topFace: {
        ...(style?.stroke && { stroke: style.stroke }),
      },
      rightFace: {
        ...(style?.stroke && { stroke: style.stroke }),
      },
      nameLabel: {
        text: name,
        ...(style?.color && { fill: style.color }),
      },
      typeLabel: {
        text: typeText,
        ...(style?.color && { fill: style.color }),
      },
      instancesLabel: {
        text: instancesText,
        ...(style?.color && { fill: style.color }),
      },
    },
    // Mark as background element (should be behind contained elements)
    z: -1,
  })

  return deploymentNode
}

/**
 * C4 InfrastructureNode shape - smaller node for specific infrastructure
 */
export const C4InfrastructureNode = joint.dia.Element.define(
  'c4.InfrastructureNode',
  {
    size: { width: 240, height: 160 },
    attrs: {
      body: {
        refWidth: '100%',
        refHeight: '100%',
        rx: 10,
        ry: 10,
        fill: '#85bbf0',
        stroke: '#5d99c6',
        strokeWidth: 2,
      },
      nameLabel: {
        refX: '50%',
        refY: 25,
        textVerticalAnchor: 'middle',
        textAnchor: 'middle',
        fontSize: 14,
        fontWeight: 'bold',
        fill: '#000000',
        fontFamily: 'Open Sans, sans-serif',
        text: '',
      },
      typeLabel: {
        refX: '50%',
        refY: 45,
        textVerticalAnchor: 'middle',
        textAnchor: 'middle',
        fontSize: 11,
        fill: '#000000',
        fontFamily: 'Open Sans, sans-serif',
        text: '[Infrastructure Node]',
      },
      descriptionLabel: {
        refX: '50%',
        refY: 75,
        textVerticalAnchor: 'top',
        textAnchor: 'middle',
        fontSize: 11,
        fill: '#000000',
        fontFamily: 'Open Sans, sans-serif',
        text: '',
        textWrap: {
          width: '90%',
          height: 70,
          ellipsis: true,
        },
      },
    },
  },
  {
    markup: [
      { tagName: 'rect', selector: 'body' },
      { tagName: 'text', selector: 'nameLabel' },
      { tagName: 'text', selector: 'typeLabel' },
      { tagName: 'text', selector: 'descriptionLabel' },
    ],
  }
)

export function createInfrastructureNode(
  id: string,
  name: string,
  technology: string = '',
  description: string = '',
  x: number = 0,
  y: number = 0,
  style?: {
    background?: string
    stroke?: string
    color?: string
  }
): joint.dia.Element {
  const typeText = technology ? `[${technology}]` : '[Infrastructure Node]'

  const infraNode = new C4InfrastructureNode({
    id,
    position: { x, y },
    attrs: {
      body: {
        ...(style?.background && { fill: style.background }),
        ...(style?.stroke && { stroke: style.stroke }),
      },
      nameLabel: {
        text: name,
        ...(style?.color && { fill: style.color }),
      },
      typeLabel: {
        text: typeText,
        ...(style?.color && { fill: style.color }),
      },
      descriptionLabel: {
        text: description,
        ...(style?.color && { fill: style.color }),
      },
    },
  })

  return infraNode
}
